/**
 * Altara Trust Utility · Provider Marketplace — LIVE.
 *
 * Route: /platform/providers
 *
 * Replaces the earlier presentational skeleton with real data from
 * `/api/trust-utility/providers`. Each provider card shows metadata,
 * live health, mode (sandbox / production), and links to the detail
 * page for config + test connection.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  ArrowLeft, ShieldCheck, Fingerprint, ScanFace, CreditCard, ClipboardCheck,
  Coins, GraduationCap, MessageCircle, Sparkles, HardDrive, Building2,
  Newspaper, Gavel, RefreshCw, ArrowRight,
} from "lucide-react";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

const CATEGORY_ICON = {
  identity:              Fingerprint,
  document_verification: ShieldCheck,
  biometrics:            ScanFace,
  address_verification:  Building2,
  pep_sanctions:         Gavel,
  adverse_media:         Newspaper,
  company_verification:  Building2,
  beneficial_ownership:  Building2,
  credit_bureau:         CreditCard,
  affordability:         ClipboardCheck,
  device_intelligence:   Fingerprint,
  responsible_gaming:    ShieldCheck,
  learning:              GraduationCap,
  ai:                    Sparkles,
};

const STATUS_TONE = {
  healthy:  { label: "Healthy",  bg: "#D1FAE5", color: "#065F46" },
  degraded: { label: "Degraded", bg: "#FEF3C7", color: "#92400E" },
  offline:  { label: "Offline",  bg: "#FEE2E2", color: "#7F1D1D" },
  unknown:  { label: "Unknown",  bg: "#F1F5F9", color: "#475569" },
};
const MODE_TONE = {
  sandbox:    { label: "Sandbox",    bg: "#FEF3C7", color: "#92400E" },
  production: { label: "Production", bg: "#D1FAE5", color: "#065F46" },
};

export default function ProviderMarketplace() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/trust-utility/providers");
      setProviders(data);
    } catch {
      toast.error("Failed to load providers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function refreshAll() {
    setRefreshing(true);
    try {
      await Promise.all(
        providers.map((p) => api.post(`/trust-utility/providers/${p.provider_id}/health`).catch(() => null)),
      );
      toast.success("Provider health refreshed");
      load();
    } finally {
      setRefreshing(false);
    }
  }

  const grouped = useMemo(() => {
    const g = {};
    for (const p of providers) {
      const cat = p.category || "identity";
      (g[cat] = g[cat] || []).push(p);
    }
    // Sort categories by count desc
    return Object.entries(g).sort((a, b) => b[1].length - a[1].length);
  }, [providers]);

  return (
    <div className="max-w-6xl mx-auto p-6 lg:p-10 space-y-6" data-testid="provider-marketplace">
      <Link to="/platform/trust-utility" data-testid="back-to-trust-utility" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="w-4 h-4" /> Trust Utility
      </Link>

      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>
            Platform · Trust Utility · Providers
          </div>
          <h1 className="font-display text-3xl font-semibold" style={{ color: NAVY }}>Provider Marketplace</h1>
          <p className="text-[15px] text-slate-600 max-w-3xl mt-2 leading-relaxed">
            Altara is provider-neutral. Every capability plugs into a swappable set of vendors — new providers register against a stable adapter interface, no code changes elsewhere.
          </p>
        </div>
        <button
          data-testid="refresh-marketplace-health"
          onClick={refreshAll}
          disabled={refreshing || loading}
          className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[13px] font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh health
        </button>
      </header>

      {loading && <div className="text-sm text-slate-500">Loading providers…</div>}

      {!loading && grouped.map(([category, list]) => {
        const Icon = CATEGORY_ICON[category] || ShieldCheck;
        return (
          <section
            key={category}
            data-testid={`provider-cat-${category}`}
            className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5 space-y-3"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${GOLD}22`, color: GOLD }}>
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display text-lg font-semibold capitalize" style={{ color: NAVY }}>
                  {category.replace(/_/g, " ")}
                </div>
                <div className="text-[11px] uppercase tracking-widest text-slate-400">
                  {list.length} provider{list.length === 1 ? "" : "s"}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 border-t border-slate-100 pt-3">
              {list.map((p) => <ProviderCard key={p.provider_id} p={p} />)}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function ProviderCard({ p }) {
  const health = p.health || { status: "unknown" };
  const tone = STATUS_TONE[health.status] || STATUS_TONE.unknown;
  const modeTone = MODE_TONE[p.mode] || MODE_TONE.sandbox;
  return (
    <Link
      data-testid={`provider-card-${p.provider_id}`}
      to={`/platform/providers/${p.provider_id}`}
      className="rounded-xl border border-slate-100 p-3 hover:border-slate-300 hover:shadow-sm transition group"
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-[14px]" style={{ color: NAVY }}>{p.name}</div>
            {p.default_provider && (
              <span className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded" style={{ background: `${GOLD}22`, color: GOLD }}>
                Default
              </span>
            )}
            <span className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded"
              style={{ background: modeTone.bg, color: modeTone.color }}>{modeTone.label}</span>
            <span className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded"
              style={{ background: tone.bg, color: tone.color }}>{tone.label}</span>
          </div>
          <div className="text-[12px] text-slate-500 mt-1 leading-relaxed line-clamp-2">
            {p.description}
          </div>
          <div className="text-[10px] text-slate-400 mt-1 font-mono">
            v{p.version} · {p.country_coverage?.join(", ")} · {p.supported_checks?.length || 0} checks
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 shrink-0 self-center" />
      </div>
    </Link>
  );
}
